import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldCheck, Loader2 } from "lucide-react";
import {
  tokenizePan,
  maskPan,
  last4Of,
  luhnValid,
  detectBrand,
  formatCardNumber,
  formatExpiry,
} from "@voult/lib/tokenizer";

const THEMES = [
  { id: "midnight", label: "Midnight", cls: "bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900" },
  { id: "emerald", label: "Emerald", cls: "bg-gradient-to-br from-emerald-900 via-teal-900 to-slate-900" },
  { id: "sunset", label: "Sunset", cls: "bg-gradient-to-br from-rose-900 via-orange-900 to-slate-900" },
  { id: "royal", label: "Royal", cls: "bg-gradient-to-br from-violet-900 via-purple-950 to-slate-900" },
  { id: "graphite", label: "Graphite", cls: "bg-gradient-to-br from-zinc-800 via-zinc-900 to-black" },
];

export default function AddCardModal({ open, onClose, onSave }) {
  const [form, setForm] = React.useState({
    cardholder_name: "",
    pan: "",
    expiry: "",
    cvc: "",
    nickname: "",
  });
  const [color_theme, setColorTheme] = React.useState("midnight");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setForm({ cardholder_name: "", pan: "", expiry: "", cvc: "", nickname: "" });
      setColorTheme("midnight");
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const valid = luhnValid(form.pan);
  const brand = detectBrand(form.pan);
  const expMatch = /^(0[1-9]|1[0-2])\/(\d{2})$/.exec(form.expiry);

  const handlePan = (v) => setForm((f) => ({ ...f, pan: formatCardNumber(v) }));
  const handleExp = (v) => setForm((f) => ({ ...f, expiry: formatExpiry(v) }));
  const handleCvc = (v) => setForm((f) => ({ ...f, cvc: v.replace(/\D/g, "").slice(0, 4) }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.cardholder_name.trim()) return setError("Cardholder name is required.");
    if (!valid) return setError("Please enter a valid card number.");
    if (!expMatch) return setError("Expiry must be in MM/YY format.");
    if (form.cvc.length < 3) return setError("CVC must be at least 3 digits.");

    setSubmitting(true);
    // Simulate tokenization request latency
    await new Promise((r) => setTimeout(r, 700));

    const token = tokenizePan(form.pan);
    const payload = {
      cardholder_name: form.cardholder_name.trim(),
      masked_number: maskPan(form.pan),
      last4: last4Of(form.pan),
      token,
      pan: form.pan,
      brand,
      expiry_month: expMatch[1],
      expiry_year: expMatch[2],
      nickname: form.nickname.trim(),
      color_theme,
    };

    try {
      await onSave(payload);
    } catch (err) {
      setError(err.response?.data?.detail?.toString() || "Could not save card.");
    }
    setSubmitting(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 40, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 20, opacity: 0 }}
          transition={{ type: "spring", stiffness: 280, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-lg overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-white">
                <ShieldCheck className="h-4 w-4" />
              </div>
              <h2 className="text-base font-semibold text-slate-900">Add card to Voult</h2>
            </div>
            <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={submit} className="space-y-5 px-6 py-5">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-600">Cardholder name</label>
              <input
                value={form.cardholder_name}
                onChange={(e) => setForm((f) => ({ ...f, cardholder_name: e.target.value }))}
                placeholder="John Doe"
                className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/5"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-600">Card number</label>
              <div className="relative">
                <input
                  value={form.pan}
                  onChange={(e) => handlePan(e.target.value)}
                  placeholder="4242 4242 4242 4242"
                  inputMode="numeric"
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 pr-20 font-mono text-sm text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/5"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {form.pan ? brand : ""}
                </span>
              </div>
              {form.pan && !valid && (
                <p className="mt-1 text-xs text-rose-500">Card number is invalid.</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Expiry</label>
                <input
                  value={form.expiry}
                  onChange={(e) => handleExp(e.target.value)}
                  placeholder="MM/YY"
                  inputMode="numeric"
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 font-mono text-sm text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/5"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">CVC</label>
                <input
                  value={form.cvc}
                  onChange={(e) => handleCvc(e.target.value)}
                  placeholder="123"
                  inputMode="numeric"
                  type="password"
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 font-mono text-sm text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/5"
                />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-600">Nickname (optional)</label>
              <input
                value={form.nickname}
                onChange={(e) => setForm((f) => ({ ...f, nickname: e.target.value }))}
                placeholder="Daily spend"
                className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/5"
              />
            </div>

            <div>
              <label className="mb-2 block text-xs font-medium text-slate-600">Card theme</label>
              <div className="flex flex-wrap gap-2">
                {THEMES.map((t) => (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => setColorTheme(t.id)}
                    className={`h-9 w-12 rounded-lg ${t.cls} ring-2 transition ${color_theme === t.id ? "ring-slate-900 ring-offset-2" : "ring-transparent"}`}
                    title={t.label}
                  />
                ))}
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-xl bg-slate-50 p-3">
              <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
              <p className="text-xs leading-relaxed text-slate-500">
                Your card number is <span className="font-semibold text-slate-700">tokenized instantly</span>. The full number is encrypted by CITIDAN; only the token and last 4 digits are shown.
              </p>
            </div>

            {error && <p className="text-sm text-rose-500">{error}</p>}

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Tokenizing…
                  </>
                ) : (
                  "Tokenize & save"
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}